// CompositionCard.tsx
import React, { useState, useEffect, useMemo } from "react";
import { CompositionSlice } from "../../models/Dashboard";

import './DashboardStyleSheet.css';

import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
} from "recharts";

type Mode = "artist" | "category" | "location";

export interface CompositionClickFilter {
    type: Mode;
    id: string;
    label: string;
}
interface CompositionCardProps {
    artistData: CompositionSlice[];
    categoryData: CompositionSlice[];
    locationData: CompositionSlice[];
    loading?: boolean;
    onFilterFromComposition?: (filter: CompositionClickFilter | null) => void;
}

type ChartDataType = {
    id: string;
    orgId: string,
    name: string;
    value: number;
    percentage?: number;
};

const COLORS = ["#111827", "#4b5563", "#6b7280", "#9ca3af", "#d1d5db", "#374151"];

const truncate = (value: string, max = 18) =>
    value.length > max ? value.slice(0, max - 1) + "…" : value;

const CompositionTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload as ChartDataType;

    return (
        <div className="chart-tooltip">
            <div className="chart-tooltip-date">{p.name}</div>
            <div className="chart-tooltip-value">
                {new Intl.NumberFormat(undefined, {
                    maximumFractionDigits: 0,
                }).format(p.value)}{" "}
                {p.percentage != null && `(${p.percentage.toFixed(1)}%)`}
            </div>
        </div>
    );
};

export const CompositionCard: React.FC<CompositionCardProps> = ({
    artistData,
    categoryData,
    locationData,
    loading,
    onFilterFromComposition,
}) => {
    const [mode, setMode] = useState<Mode>("artist");
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const chartData: ChartDataType[] = useMemo(() => {
        const map = (data: CompositionSlice[] | undefined | null): ChartDataType[] =>
            (data ?? []).map((d, index) => ({
                // build a unique id per mode and item
                id: `${mode}-${(d as any).id ?? d.label ?? index}`,
                orgId: d.id,
                name: d.label,
                value: d.value,
                percentage: d.percentage,
            }));

        switch (mode) {
            case "artist":
                return map(artistData);
            case "category":
                return map(categoryData);
            case "location":
                return map(locationData);
            default:
                return map(artistData);
        }
    }, [mode, artistData, categoryData, locationData]);

    const hasData = chartData.length > 0;

    const handleItemClick = (entry: ChartDataType) => {

        if (!onFilterFromComposition) return;

        // toggle behaviour
        if (selectedId === entry.id) {
            setSelectedId(null);
            onFilterFromComposition(null);
        } else {
            setSelectedId(entry.id);
            onFilterFromComposition({
                type: mode,
                id: entry.orgId,
                label: entry.name,
            });
        }
    };

    return (
        <div className="dashboard-card">
            <div className="dashboard-card-header">
                <h2>Portfolio Composition (Top 10)</h2>
                <div className="segmented-control">
                    <button
                        className={mode === "artist" ? "active" : ""}
                        onClick={() => setMode("artist")}
                    >
                        Artist
                    </button>
                    <button
                        className={mode === "category" ? "active" : ""}
                        onClick={() => setMode("category")}
                    >
                        Category
                    </button>
                    <button
                        className={mode === "location" ? "active" : ""}
                        onClick={() => setMode("location")}
                    >
                        Location
                    </button>
                </div>
            </div>
            <div className="dashboard-card-body composition-body">
                {loading && !hasData && <p>Loading composition…</p>}
                {!loading && !hasData && (
                    <p className="empty-state">No composition data available.</p>
                )}

                {hasData && (
                    <div className="composition-layout">
                        {/* Chart */}
                        <div className="composition-chart">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={45}
                                        outerRadius={70}
                                        paddingAngle={1}
                                        onClick={(_, index) => handleItemClick(chartData[index])}
                                    >
                                        {chartData.map((entry, index) => {
                                            const isSelected = entry.id === selectedId;
                                            return (
                                                <Cell
                                                    key={entry.id}
                                                    fill={COLORS[index % COLORS.length]}
                                                    stroke={isSelected ? "#111827" : "#ffffff"}
                                                    strokeWidth={isSelected ? 3 : 1}
                                                    cursor="pointer"
                                                />
                                            );
                                        })}
                                    </Pie>
                                    <Tooltip content={<CompositionTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Legend */}
                        <div className="composition-legend">
                            {chartData.map((entry, index) => {
                                const isSelected = entry.id === selectedId;
                                return (
                                    <button
                                        key={entry.id}
                                        type="button"
                                        className={`composition-legend-item ${isSelected ? "composition-legend-item--selected" : ""
                                            }`}
                                        onClick={() => handleItemClick(entry)}
                                    >
                                        <span
                                            className="composition-legend-swatch"
                                            style={{
                                                backgroundColor: COLORS[index % COLORS.length],
                                            }}
                                        />
                                        <span className="composition-legend-label">
                                            {truncate(entry.name)}
                                        </span>
                                        {entry.percentage != null && (
                                            <span className="composition-legend-value">
                                                {entry.percentage.toFixed(1)}%
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default CompositionCard;

